<script setup lang="ts">
import type { PageSection } from "@/apps/page/page.interfaces";
import {
    BlockDefinition,
    BlockDefinitionGroup,
    BlockDefWithType,
    BlockTypeGroup,
} from "@/apps/page/services/blocks/block.interfaces";
import { useBlocks } from "../../../../services/blocks/useBlocks";
import { useFocusOnEditSection } from "../../../../services/useFocusOnEditSection";
import cSaveButton from "@/shared/components/cSaveButton.vue";
import { BlockBaseElement, BlockDataValue } from "@/views/blocks/PageBlock.interface";
import { ButtonSecondaryIconSm } from "@easy/ui/buttons";
import { CardToggle } from "@easy/ui/cards";
import { FormSwitch } from "@easy/ui/forms";
import { ArrowLeftIcon } from "@easy/ui/icons";
import { PageSidebar } from "@easy/ui/page-components";
import { computed } from "vue";
import { t } from "@/i18n";
import PageColors from "@/apps/page/project/sidebar/sections/PageColors.vue";
import BlockFieldGroups from "./components/BlockFieldGroups.vue";
import { ThemeColors } from "@/shared/themes/Theme.interface";
import { useActions, useState } from "@/store/vuex-composition-mappers";
import { defaultSaveModalConfig } from "@/shared/modals/default-save-modal.config";

const { section } = defineProps<{
    section: PageSection;
}>();
const emit = defineEmits(["saveBlock", "cancelBlock"]);
const selectedTab = defineModel("selectedTab", { type: String, default: "content" });

// @ts-expect-error - TODO MJ: Fix types
const { hasChanges } = useState("page", ["hasChanges"]);
// @ts-expect-error - TODO MJ: Fix types
const { showConfirmModal } = useActions("modals", ["showConfirmModal"]);
const { getBlockWithTypeMetadata, groupTypes } = useBlocks();
const blockWithMetadata = computed<BlockDefWithType | null>(() =>
    section.typeId ? getBlockWithTypeMetadata(section.typeId, section.type) : null
);

const groupedDataFields = computed(() => {
    return groupTypes.value.reduce<
        Record<BlockDefinitionGroup, BlockDefinition<BlockBaseElement<BlockDataValue>>[]>
    >(
        (
            acc: Record<BlockDefinitionGroup, BlockDefinition<BlockBaseElement<BlockDataValue>>[]>,
            type: BlockTypeGroup
        ) => {
            const fields =
                blockWithMetadata.value?.block?.dataFields.filter(
                    (field) => field.group === type.group
                ) ?? [];

            acc[type.group] = fields;

            return acc;
        },
        {} as Record<BlockDefinitionGroup, BlockDefinition<BlockBaseElement<BlockDataValue>>[]>
    );
});

const styleGroupNames = ["formatting"];

const contentGroupTypes = computed(() =>
    groupTypes.value.filter((gt) => !styleGroupNames.includes(gt.group))
);

const stylesGroupTypes = computed(() =>
    groupTypes.value.filter((gt) => styleGroupNames.includes(gt.group))
);

const saveButtonText = computed(() => t("shared.save"));

const tabs = computed(() => [
    {
        label: t("page.block.content"),
        value: "content",
    },
    {
        label: t("page.block.styles"),
        value: "styles",
    },
]);

useFocusOnEditSection();

async function saveBlock() {
    if (!section._created) {
        section._changed = true;
    }
    emit("saveBlock");
}

async function cancelBlock() {
    if (hasChanges.value) {
        const action = await showConfirmModal(defaultSaveModalConfig());
        if (action) {
            return saveBlock();
        }
    }
    emit("cancelBlock");
}
</script>

<template>
    <PageSidebar>
        <template #sidebarTitle>
            <div class="flex items-center space-x-3">
                <ButtonSecondaryIconSm @click="cancelBlock()">
                    <ArrowLeftIcon />
                </ButtonSecondaryIconSm>
                <span class="truncate text-lg">
                    {{ $t("page.sections.edit_section") }} -
                    {{ blockWithMetadata?.blockType.title }}
                </span>
            </div>
        </template>
        <template #sidebarMain>
            <div class="p-6">
                <FormSwitch v-model="selectedTab" :options="tabs" />
            </div>
            <template v-if="selectedTab === 'content'">
                <BlockFieldGroups
                    :group-types="contentGroupTypes"
                    :grouped-data-fields="groupedDataFields"
                    :section="section"
                />
            </template>
            <div v-else-if="selectedTab === 'styles'">
                <CardToggle :title="$t('page.block.colors')">
                    <div class="space-y-3">
                        <div
                            v-for="field in blockWithMetadata?.block?.styleFields"
                            :key="field.value"
                        >
                            <PageColors
                                v-if="field.type === 'color'"
                                :title="field.name"
                                v-model="section.styleValues[field.value as keyof ThemeColors]"
                            />
                        </div>
                    </div>
                </CardToggle>
                <BlockFieldGroups
                    :group-types="stylesGroupTypes"
                    :grouped-data-fields="groupedDataFields"
                    :section="section"
                    :show-visibility-toggle="false"
                />
            </div>
        </template>
        <template #sidebarFooter>
            <cSaveButton
                @save="saveBlock"
                :disabled="!hasChanges"
                :ctaEnabled="saveButtonText"
                :ctaDisabled="saveButtonText"
            />
        </template>
    </PageSidebar>
</template>
